import Vue from "vue";

const FEATURE_FLAGS_BY_TIER = {
  free: {
    mpr: true,
    volume_rendering: true,
    segmentation: false,
    surfaces_3d: false,
    pacs: false,
    ai: false,
    anonymization: false,
    mask_export: false,
    cine: false,
    viewport_export: false
  },
  base: {
    mpr: true,
    volume_rendering: true,
    segmentation: true,
    surfaces_3d: false,
    pacs: false,
    ai: false,
    anonymization: true,
    mask_export: false,
    cine: true,
    viewport_export: true
  },
  pro: {
    mpr: true,
    volume_rendering: true,
    segmentation: true,
    surfaces_3d: true,
    pacs: true,
    ai: true,
    anonymization: true,
    mask_export: true,
    cine: true,
    viewport_export: true
  },
  enterprise: {
    mpr: true,
    volume_rendering: true,
    segmentation: true,
    surfaces_3d: true,
    pacs: true,
    ai: true,
    anonymization: true,
    mask_export: true,
    cine: true,
    viewport_export: true
  }
};

const FEATURE_FLAGS_ALL_ENABLED = {
  mpr: true,
  volume_rendering: true,
  segmentation: true,
  surfaces_3d: true,
  pacs: true,
  ai: true,
  anonymization: true,
  mask_export: true,
  cine: true,
  viewport_export: true
};

const QUOTA_LIMITS_BY_TIER = {
  free: { share_links: 10, drop_links: 10 },
  base: { share_links: 100, drop_links: 100 },
  pro: { share_links: -1, drop_links: -1 },
  enterprise: { share_links: -1, drop_links: -1 }
};

const normalizeTier = tier => {
  const value = (tier || "free").toLowerCase();
  return FEATURE_FLAGS_BY_TIER[value] ? value : "free";
};

const isBillingGloballyDisabled = () =>
  String(process.env.BILLING_MODE || "")
    .trim()
    .toLowerCase() === "disabled";

const DISABLED_BILLING_CATALOG = Object.freeze({
  billing_enabled: false,
  tiers: [],
  enterprise: {}
});

export default {
  namespaced: true,
  state: () => ({
    catalog: null,
    features: null,
    quota: null,
    ingestionQuotaExceeded: null,
    loading: false
  }),
  getters: {
    catalog: state => state.catalog,
    isBillingEnabled: state => {
      if (isBillingGloballyDisabled()) return false;
      return state.catalog ? Boolean(state.catalog.billing_enabled) : false;
    },
    subscriptionTier: (_state, _getters, rootState) =>
      normalizeTier(rootState.auth.user?.subscription_tier),
    featureFlags: (state, getters) => {
      if (state.catalog && !getters.isBillingEnabled) {
        return FEATURE_FLAGS_ALL_ENABLED;
      }
      return state.features || FEATURE_FLAGS_BY_TIER[getters.subscriptionTier];
    },
    hasFeature: (_state, getters) => featureName =>
      Boolean(getters.featureFlags[featureName]),
    quotaLimits: (state, getters) => {
      if (state.catalog && !getters.isBillingEnabled) {
        return { share_links: -1, drop_links: -1 };
      }
      return (
        (state.quota && state.quota.limits) ||
        QUOTA_LIMITS_BY_TIER[getters.subscriptionTier]
      );
    },
    quotaUsage: state =>
      (state.quota && state.quota.usage) || { share_links: 0, drop_links: 0 },
    quotaRemaining: (state, getters) => quotaKey => {
      if (state.quota && state.quota.remaining) {
        return state.quota.remaining[quotaKey];
      }
      const limit = getters.quotaLimits[quotaKey];
      const used = getters.quotaUsage[quotaKey] || 0;
      return limit === -1 ? -1 : Math.max(0, limit - used);
    },
    quotaResetAt: state => (state.quota ? state.quota.reset_at : null),
    aiQuotaResetAt: state =>
      state.quota ? state.quota.ai_quota_reset_at : null,
    ingestionQuota: state => (state.quota ? state.quota.ingestion : null),
    ingestionQuotaExceeded: state => state.ingestionQuotaExceeded
  },
  mutations: {
    setFeatures(state, payload) {
      state.features = (payload && payload.features) || null;
    },
    setCatalog(state, payload) {
      state.catalog = payload || null;
    },
    setQuota(state, payload) {
      state.quota = payload || null;
    },
    setIngestionQuotaExceeded(state, payload) {
      state.ingestionQuotaExceeded = payload || null;
    },
    setLoading(state, value) {
      state.loading = value;
    },
    reset(state) {
      state.catalog = null;
      state.features = null;
      state.quota = null;
      state.ingestionQuotaExceeded = null;
      state.loading = false;
    }
  },
  actions: {
    async fetchBillingCatalog({ commit, rootGetters }) {
      if (!rootGetters["auth/isAuth"]) {
        console.warn("Skipping billing catalog fetch: not authenticated.");
        commit("setCatalog", null);
        return null;
      }
      if (isBillingGloballyDisabled()) {
        commit("setCatalog", DISABLED_BILLING_CATALOG);
        return DISABLED_BILLING_CATALOG;
      }
      const data = await Vue.$http.get("api/billing-catalog/");
      commit("setCatalog", data);
      return data;
    },
    async fetchUserFeatures({ commit, rootGetters }) {
      if (!rootGetters["auth/isAuth"]) {
        console.warn("Skipping user features fetch: not authenticated.");
        commit("setFeatures", null);
        return null;
      }
      if (isBillingGloballyDisabled()) {
        commit("setFeatures", null);
        return null;
      }
      const data = await Vue.$http.get("api/user-features/");
      commit("setFeatures", data);
      return data;
    },
    async fetchQuotaUsage({ state, commit, rootGetters }) {
      if (!rootGetters["auth/isAuth"]) {
        console.warn("Skipping quota usage fetch: not authenticated.");
        commit("setQuota", null);
        return null;
      }
      if (isBillingGloballyDisabled()) {
        commit("setQuota", null);
        return null;
      }
      const data = await Vue.$http.get("api/quota-usage/");
      commit("setQuota", data);
      const blockedRequest = Number(state.ingestionQuotaExceeded?.requested_bytes);
      const remaining = Number(data?.ingestion?.remaining_bytes);
      // Retain the actionable rejection until the requested upload would fit
      // again. A periodic read alone does not mean the same upload is allowed.
      if (
        Number.isFinite(blockedRequest) &&
        Number.isFinite(remaining) &&
        (remaining === -1 || remaining >= blockedRequest)
      ) {
        commit("setIngestionQuotaExceeded", null);
      }
      return data;
    },
    async refreshBillingData({ dispatch, commit, rootGetters }) {
      if (!rootGetters["auth/isAuth"]) {
        console.warn("Skipping billing refresh: not authenticated.");
        commit("reset");
        return;
      }
      if (isBillingGloballyDisabled()) {
        commit("setCatalog", DISABLED_BILLING_CATALOG);
        commit("setFeatures", null);
        commit("setQuota", null);
        return;
      }
      commit("setLoading", true);
      try {
        const catalog = await dispatch("fetchBillingCatalog");
        if (catalog && !catalog.billing_enabled) {
          commit("setFeatures", null);
          commit("setQuota", null);
          return;
        }
        await Promise.all([
          dispatch("fetchUserFeatures"),
          dispatch("fetchQuotaUsage")
        ]);
      } finally {
        commit("setLoading", false);
      }
    }
  }
};
